import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  TextField,
  Typography,
} from "@mui/material";

import { Can } from "src/helpers/Auth";
import {
  useShadowRecordFeedback,
  useSubmitShadowRecordFeedback,
} from "./ShadowBetaServices";

const FeedbackList = ({ feedback }) => {
  if (!feedback?.length) {
    return null;
  }
  return (
    <Box sx={{ mb: 1 }} data-testid="cap2-feedback-list">
      {feedback.map((entry) => (
        <Box key={entry.id} sx={{ mb: 0.5 }}>
          <Typography variant="body2">{entry.feedback_text}</Typography>
          <Typography variant="caption" color="text.secondary">
            {entry.create_date
              ? new Date(entry.create_date).toLocaleString()
              : null}
            {entry.field_corrections &&
              " · corrected: " +
                Object.entries(entry.field_corrections)
                  .map(([field, labels]) => `${field} → ${labels.join("; ")}`)
                  .join(", ")}
          </Typography>
        </Box>
      ))}
      <Divider sx={{ my: 1 }} />
    </Box>
  );
};

FeedbackList.propTypes = {
  feedback: PropTypes.array,
};

const FeedbackForm = ({ templateId, sourceUid }) => {
  const [text, setText] = useState("");
  const { mutate: submit, isLoading: isSubmitting } =
    useSubmitShadowRecordFeedback();

  // Natural-language only, per the mockup. Structured field corrections
  // (what actually moves the accuracy numbers) remain API-only until the
  // incorporate-through-policy mechanism exists.
  const handleSubmit = () => {
    submit(
      {
        templateId,
        sourceUid,
        feedback: { feedback_text: text, field_corrections: null },
      },
      { onSuccess: () => setText("") },
    );
  };

  return (
    <Box data-testid="cap2-feedback-form">
      <TextField
        fullWidth
        multiline
        minRows={2}
        size="small"
        value={text}
        onChange={(event) => setText(event.target.value)}
        placeholder={
          "e.g. Agree this should be CAQ - the leak-on-safety-related rule " +
          "is correct. But owner group should be MAINT, not CHEM."
        }
        inputProps={{ "data-testid": "cap2-feedback-text" }}
        sx={{ mb: 1 }}
      />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="caption" color="text.secondary">
          ◈ Audit feedback only · not written to SoR
        </Typography>
        <Button
          variant="contained"
          size="small"
          disabled={isSubmitting || !text.trim()}
          onClick={handleSubmit}
          data-testid="cap2-feedback-submit"
        >
          {isSubmitting ? "Submitting..." : "Submit feedback"}
        </Button>
      </Box>
    </Box>
  );
};

FeedbackForm.propTypes = {
  templateId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  sourceUid: PropTypes.string.isRequired,
};

/**
 * Feedback panel for one shadow-screened CR: prior feedback (visible to all
 * readers), and - for users who can update automation configs (CAP Admin) -
 * a natural-language box. Feedback is audit commentary; accuracy charts
 * score against the linked ground-truth dataset. Nothing is written to the
 * CAP SoR.
 */
export const PredictionFeedback = ({ templateId, sourceUid }) => {
  const { data: feedback } = useShadowRecordFeedback(templateId, sourceUid);
  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, mt: 1 }}
      data-testid="cap2-feedback"
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Typography variant="overline" color="text.secondary">
          Provide feedback on this prediction
        </Typography>
        <Chip size="small" variant="outlined" label="CAP ADMIN" />
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 1 }}
      >
        Feedback is incorporated through policy and lives on this record; there
        is no separate queue. Accuracy charts score against the template&apos;s
        ground-truth dataset.
      </Typography>
      <FeedbackList feedback={feedback} />
      <Can
        perform="autoconfig:update"
        yes={() => (
          <FeedbackForm templateId={templateId} sourceUid={sourceUid} />
        )}
      />
    </Paper>
  );
};

PredictionFeedback.propTypes = {
  templateId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  sourceUid: PropTypes.string.isRequired,
};

export default PredictionFeedback;
